import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Upload, Search, Copy, Archive, ArchiveRestore, Download } from "lucide-react";
import ImportBundledAssets from "@/components/admin/ImportBundledAssets";
import AdminLayout from "@/components/admin/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import { uploadMediaFile, type MediaAsset, getDisplayUrl, MediaThumb } from "@/components/admin/MediaPicker";

type ArchiveFilter = "active" | "archived" | "all";

const AdminMedia = () => {
  const { user } = useAdminAuth();
  const [rows, setRows] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<ArchiveFilter>("active");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<MediaAsset | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importerOpen, setImporterOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    document.title = "Media Library | Hadassah Homes Admin";
    void load();
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from("media_assets")
      .select("*")
      .order("created_at", { ascending: false });
    if (err) setError(err.message);
    else setRows((data ?? []) as MediaAsset[]);
    setLoading(false);
  };

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (filter === "active" && r.is_archived) return false;
      if (filter === "archived" && !r.is_archived) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const hit =
          r.file_name?.toLowerCase().includes(q) ||
          r.alt_text?.toLowerCase().includes(q) ||
          r.caption?.toLowerCase().includes(q) ||
          r.usage_label?.toLowerCase().includes(q);
        if (!hit) return false;
      }
      return true;
    });
  }, [rows, filter, search]);

  const handleUpload = async (f: File) => {
    setUploading(true);
    try {
      const asset = await uploadMediaFile(f, user?.id ?? null);
      setRows((prev) => [asset, ...prev]);
      toast.success("Uploaded");
      setEditing(asset);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const toggleArchive = async (row: MediaAsset) => {
    const { data, error: err } = await supabase
      .from("media_assets")
      .update({ is_archived: !row.is_archived, updated_by: user?.id ?? null } as never)
      .eq("id", row.id)
      .select()
      .single();
    if (err) return toast.error(err.message);
    setRows((prev) => prev.map((r) => (r.id === row.id ? (data as MediaAsset) : r)));
    toast.success(row.is_archived ? "Restored" : "Archived");
  };

  const copyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("URL copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    setSaving(true);
    const { data, error: err } = await supabase
      .from("media_assets")
      .update({
        alt_text: editing.alt_text?.trim() || null,
        caption: editing.caption?.trim() || null,
        usage_label: editing.usage_label?.trim() || null,
        updated_by: user?.id ?? null,
      } as never)
      .eq("id", editing.id)
      .select()
      .single();
    setSaving(false);
    if (err) return toast.error(err.message);
    setRows((prev) => prev.map((r) => (r.id === editing.id ? (data as MediaAsset) : r)));
    toast.success("Saved");
    setEditing(null);
  };

  return (
    <AdminLayout
      title="Media Library"
      description="Upload reusable images for events, team profiles, testimonials, and properties. Archiving hides the asset from pickers but does not delete the file."
    >
      <div className="mx-auto max-w-7xl space-y-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-1 flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search filename, alt, caption, label"
                className="rounded-none pl-9"
              />
            </div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as ArchiveFilter)}
              className="h-10 rounded-none border border-input bg-background px-3 text-sm"
            >
              <option value="active">Active</option>
              <option value="archived">Archived</option>
              <option value="all">All</option>
            </select>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleUpload(f);
            }}
          />
          <Button
            variant="outline"
            onClick={() => setImporterOpen(true)}
            className="rounded-none"
          >
            <Download className="mr-2 h-4 w-4" />
            Import site assets
          </Button>
          <Button
            variant="gold"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            className="rounded-none"
          >
            {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
            Upload image
          </Button>
        </div>

        <div className="border border-border bg-card p-4">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="p-6 text-destructive">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="space-y-2 p-12 text-center text-sm text-muted-foreground">
              <p>No media found. Upload an image to get started.</p>
              <p className="text-xs">
                Only images uploaded or imported into the Media Library appear here. Existing
                coded site images must be imported before they can be reused in admin — use{" "}
                <button
                  type="button"
                  className="underline underline-offset-2"
                  onClick={() => setImporterOpen(true)}
                >
                  Import site assets
                </button>
                .
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {filtered.map((r) => (
                <div key={r.id} className="group border border-border bg-background">
                  <button
                    type="button"
                    onClick={() => setEditing(r)}
                    className="block aspect-square w-full overflow-hidden bg-muted/40"
                  >
                    <MediaThumb
                      asset={r}
                      className={`h-full w-full transition ${
                        r.is_archived ? "opacity-40" : "group-hover:scale-105"
                      }`}
                    />
                  </button>
                  <div className="space-y-1 p-2">
                    <p className="truncate text-xs text-foreground" title={r.file_name ?? ""}>
                      {r.file_name ?? "Untitled"}
                    </p>
                    {r.usage_label ? (
                      <p className="truncate text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                        {r.usage_label}
                      </p>
                    ) : null}
                    <div className="flex gap-1 pt-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyUrl(getDisplayUrl(r))}
                        className="h-7 flex-1 rounded-none px-2 text-[11px]"
                      >
                        <Copy className="mr-1 h-3 w-3" />
                        URL
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleArchive(r)}
                        className="h-7 rounded-none px-2 text-[11px]"
                      >
                        {r.is_archived ? <ArchiveRestore className="h-3 w-3" /> : <Archive className="h-3 w-3" />}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          {filtered.length} of {rows.length} assets
        </p>
      </div>

      <Sheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          {editing ? (
            <form onSubmit={handleSave}>
              <SheetHeader>
                <SheetTitle className="font-headline text-2xl">Edit asset</SheetTitle>
                <SheetDescription>
                  Alt text, caption, and usage labels help with SEO and reuse.
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-5">
                <div className="max-h-64 w-full border border-border bg-muted">
                  <MediaThumb asset={editing} fit="contain" className="max-h-64 w-full" />
                </div>
                <div className="space-y-1 text-xs text-muted-foreground">
                  <p>{editing.file_name}</p>
                  <p className="break-all opacity-60">{getDisplayUrl(editing)}</p>
                  <p>
                    {editing.width && editing.height
                      ? `${editing.width}×${editing.height} · `
                      : ""}
                    {editing.file_type ?? ""}{" "}
                    {editing.file_size ? `· ${(editing.file_size / 1024).toFixed(0)} KB` : ""}
                  </p>
                  <button
                    type="button"
                    onClick={() => copyUrl(getDisplayUrl(editing))}
                    className="inline-flex items-center gap-1 underline underline-offset-2"
                  >
                    <Copy className="h-3 w-3" /> Copy public URL
                  </button>
                </div>

                <Field label="Alt text (recommended for SEO/accessibility)">
                  <Input
                    value={editing.alt_text ?? ""}
                    onChange={(e) =>
                      setEditing((prev) => (prev ? { ...prev, alt_text: e.target.value } : prev))
                    }
                    className="rounded-none"
                  />
                </Field>

                <Field label="Caption">
                  <Textarea
                    rows={2}
                    value={editing.caption ?? ""}
                    onChange={(e) =>
                      setEditing((prev) => (prev ? { ...prev, caption: e.target.value } : prev))
                    }
                    className="rounded-none"
                  />
                </Field>

                <Field label="Usage label (e.g. hero, event-banner)">
                  <Input
                    value={editing.usage_label ?? ""}
                    onChange={(e) =>
                      setEditing((prev) => (prev ? { ...prev, usage_label: e.target.value } : prev))
                    }
                    className="rounded-none"
                  />
                </Field>

                <div className="flex gap-2 pt-2">
                  <Button type="submit" variant="gold" disabled={saving} className="rounded-none">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => toggleArchive(editing)}
                    className="rounded-none"
                  >
                    {editing.is_archived ? "Restore" : "Archive"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditing(null)}
                    className="rounded-none"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </form>
          ) : null}
        </SheetContent>
      </Sheet>

      <ImportBundledAssets
        open={importerOpen}
        onOpenChange={setImporterOpen}
        onImported={(assets) => setRows((prev) => [...assets, ...prev])}
      />
    </AdminLayout>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="mb-2 block text-xs uppercase tracking-[0.14em] text-muted-foreground">
      {label}
    </label>
    {children}
  </div>
);

export default AdminMedia;
